// Sci-fi warp / portal sound synthesized with the Web Audio API.
// Rising frequency sweep with harmonic layers + subtle metallic ring +
// gentle filtered noise wind. Quiet by default (~30% of previous version).

export function playCosmicWarp() {
  try {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) return;
    const ctx = new Ctx();
    const now = ctx.currentTime;
    const dur = 2.0;

    // ---------- Master bus ----------
    const master = ctx.createGain();
    master.gain.setValueAtTime(0.0001, now);
    master.gain.exponentialRampToValueAtTime(0.28, now + 0.15);
    master.gain.exponentialRampToValueAtTime(0.22, now + 1.3);
    master.gain.exponentialRampToValueAtTime(0.0001, now + dur);
    master.connect(ctx.destination);

    // High-pass to keep it clean and un-muddy — not bass-heavy
    const hp = ctx.createBiquadFilter();
    hp.type = "highpass";
    hp.frequency.value = 120;
    hp.connect(master);

    // Stereo delay for spacious sci-fi tail
    const delay = ctx.createDelay(1.0);
    delay.delayTime.value = 0.22;
    const fb = ctx.createGain();
    fb.gain.value = 0.38;
    delay.connect(fb).connect(delay);
    const wet = ctx.createGain();
    wet.gain.value = 0.35;
    delay.connect(wet).connect(hp);

    // ---------- 1) Classic warp sweep: rising sine ----------
    // Rises from 220Hz → 2200Hz then quickly resolves down to 440Hz
    const sweep = ctx.createOscillator();
    sweep.type = "sine";
    sweep.frequency.setValueAtTime(220, now);
    sweep.frequency.exponentialRampToValueAtTime(2200, now + 1.1);
    sweep.frequency.exponentialRampToValueAtTime(440, now + dur);
    const sweepGain = ctx.createGain();
    sweepGain.gain.setValueAtTime(0.0001, now);
    sweepGain.gain.exponentialRampToValueAtTime(0.28, now + 0.2);
    sweepGain.gain.exponentialRampToValueAtTime(0.001, now + dur);
    sweep.connect(sweepGain);
    sweepGain.connect(hp);
    sweepGain.connect(delay);
    sweep.start(now);
    sweep.stop(now + dur);

    // ---------- 2) Harmonic fifth (adds sci-fi shimmer) ----------
    const fifth = ctx.createOscillator();
    fifth.type = "sine";
    fifth.frequency.setValueAtTime(330, now);
    fifth.frequency.exponentialRampToValueAtTime(3300, now + 1.1);
    fifth.frequency.exponentialRampToValueAtTime(660, now + dur);
    const fifthGain = ctx.createGain();
    fifthGain.gain.setValueAtTime(0.0001, now);
    fifthGain.gain.exponentialRampToValueAtTime(0.14, now + 0.25);
    fifthGain.gain.exponentialRampToValueAtTime(0.001, now + dur);
    fifth.connect(fifthGain);
    fifthGain.connect(hp);
    fifthGain.connect(delay);
    fifth.start(now);
    fifth.stop(now + dur);

    // ---------- 3) Metallic ring (bandpass-filtered square) ----------
    const ring = ctx.createOscillator();
    ring.type = "square";
    ring.frequency.setValueAtTime(90, now);
    ring.frequency.exponentialRampToValueAtTime(900, now + 1.1);
    ring.frequency.exponentialRampToValueAtTime(180, now + dur);
    const ringFilter = ctx.createBiquadFilter();
    ringFilter.type = "bandpass";
    ringFilter.Q.value = 18;
    ringFilter.frequency.setValueAtTime(1200, now);
    ringFilter.frequency.exponentialRampToValueAtTime(3200, now + 1.0);
    ringFilter.frequency.exponentialRampToValueAtTime(800, now + dur);
    const ringGain = ctx.createGain();
    ringGain.gain.setValueAtTime(0.0001, now);
    ringGain.gain.exponentialRampToValueAtTime(0.06, now + 0.35);
    ringGain.gain.exponentialRampToValueAtTime(0.001, now + dur);
    ring.connect(ringFilter);
    ringFilter.connect(ringGain);
    ringGain.connect(hp);
    ringGain.connect(delay);
    ring.start(now);
    ring.stop(now + dur);

    // ---------- 4) Subtle filtered noise wind ----------
    const noiseBuf = ctx.createBuffer(1, ctx.sampleRate * dur, ctx.sampleRate);
    const nd = noiseBuf.getChannelData(0);
    for (let i = 0; i < nd.length; i++) {
      nd[i] = (Math.random() * 2 - 1) * 0.5;
    }
    const noise = ctx.createBufferSource();
    noise.buffer = noiseBuf;
    const noiseFilter = ctx.createBiquadFilter();
    noiseFilter.type = "bandpass";
    noiseFilter.Q.value = 2.5;
    noiseFilter.frequency.setValueAtTime(600, now);
    noiseFilter.frequency.exponentialRampToValueAtTime(4000, now + 1.0);
    noiseFilter.frequency.exponentialRampToValueAtTime(500, now + dur);
    const noiseGain = ctx.createGain();
    noiseGain.gain.setValueAtTime(0.0001, now);
    noiseGain.gain.exponentialRampToValueAtTime(0.08, now + 0.4);
    noiseGain.gain.exponentialRampToValueAtTime(0.001, now + dur);
    noise.connect(noiseFilter);
    noiseFilter.connect(noiseGain);
    noiseGain.connect(hp);
    noiseGain.connect(delay);
    noise.start(now);
    noise.stop(now + dur);

    // Auto-close audio context after playback
    setTimeout(() => {
      try {
        ctx.close();
      } catch (_) {}
    }, (dur + 0.4) * 1000);
  } catch (_) {
    // Silent fail — sound is decorative
  }
}

export default playCosmicWarp;
